import { createStyles, Group, Stack, Text } from '@mantine/core';
import { isBoolean, noop } from 'lodash/fp';
import React, { useMemo } from 'react';

import { ReactComponent as StoreWithoutBanner } from '../../../../assets/img/store-no-banner.svg';
import { ReactComponent as StoreWithBanner } from '../../../../assets/img/store-with-banner.svg';
import { useBrandingContext } from '../../branding.context';

interface StoreBannerSelectorProps {
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

export function StoreBannerSelector({
  onChange,
  disabled = false,
}: StoreBannerSelectorProps) {
  const { classes, cx, theme } = useStyles();
  const { defaultValues, portalConfig } = useBrandingContext();

  const isWithBanner = useMemo(() => {
    if (!isBoolean(portalConfig.design?.store?.with_banner)) {
      return defaultValues.design.store.with_banner;
    }

    return portalConfig.design?.store?.with_banner;
  }, [
    defaultValues.design.store.with_banner,
    portalConfig.design?.store?.with_banner,
  ]);

  return (
    <Group grow>
      <Stack
        p="xs"
        align="center"
        onClick={() => (disabled ? noop : onChange(false))}
        className={cx(classes.section, {
          selected: !isWithBanner,
          disabled,
        })}
      >
        <StoreWithoutBanner
          color={
            isWithBanner || disabled
              ? theme.colors.gray[3]
              : theme.colors.blue_accent[1]
          }
        />

        <Text>None</Text>
      </Stack>

      <Stack
        p="xs"
        align="center"
        onClick={() => (!disabled ? noop : onChange(true))}
        className={cx(classes.section, {
          selected: isWithBanner,
          disabled,
        })}
      >
        <StoreWithBanner
          color={
            isWithBanner || !disabled
              ? theme.colors.blue_accent[1]
              : theme.colors.gray[3]
          }
        />

        <Text>Mini Banner</Text>
      </Stack>
    </Group>
  );
}

const useStyles = createStyles((theme) => ({
  section: {
    borderRadius: theme.radius.md,
    border: `1px solid transparent`,
    cursor: 'pointer',
    transition: 'border-color 0.15s ease-in-out',

    '&.selected': {
      borderColor: theme.colors.blue_accent[4],
    },

    '&.disabled': {
      pointerEvents: 'none',
      borderColor: 'white',
      opacity: 0.7,
    },

    svg: {
      '*': {
        transition: 'fill 0.15s ease-in-out',
      },
    },
  },
}));
